import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useCookies } from "react-cookie";
import Container from "react-bootstrap/Container";
import NavBar from "../components/NavBar";
import { NavigateRoutes } from "../constants/routes";
import {
  tokenIsValid,
  getTokenExpirationDate
} from "../utils/token";
import { authorize } from "../api/Login";
import styles from "../styles/Profile.module.css";

const NeedToKnowPage = () => {
  const navigate = useNavigate();
  const [cookies, setCookie] = useCookies(["token", "firebaseToken"]);
  const [authorized, setAuthorized] = useState();
  const [pageDoneLoading, setPageDoneLoading] = useState(false);
  const [logoutTimeout, setLogoutTimeout] = useState();
  const [authTimeout, setAuthTimeout] = useState();
  const [reauthorized, setReauthorized] = useState(false);

  const redirectLogin = useCallback(
    () => navigate(NavigateRoutes.LOGIN + "?redirect=PROFILE"),
    [navigate]
  );

  useEffect(() => {
    document.title = "Project DUAL Apps: Need To Know";
    if (!cookies["token"]) {
      setAuthorized(false);
    } else {
      authorize(cookies["firebaseToken"]).then((res) => {
        setCookie("token", res.data.token);
        setReauthorized(true);
      });
    }
    return () => {
      clearTimeout(logoutTimeout);
      clearTimeout(authTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (reauthorized) {
      const auth = tokenIsValid(cookies["token"]);
      setAuthorized(auth);
      const exp = getTokenExpirationDate(cookies["token"]);
      const now = new Date();
      const newTimeout = setTimeout(
        () => setAuthorized(false),
        exp - now - (1 * 10 * 1000)
      );
      setAuthTimeout(newTimeout);
      setPageDoneLoading(true);
    }
  }, [cookies, reauthorized]);

  useEffect(() => {
    if (!authorized && pageDoneLoading) {
      const newTimeout = setTimeout(() => redirectLogin(), 5000);
      setLogoutTimeout(newTimeout);
    }
  }, [authorized, pageDoneLoading, redirectLogin]);

  return (
    <div>
      <NavBar />
      {pageDoneLoading && (
        <Container className={styles.pageContainer}>
          <Container>
            <h1>Need to Know</h1>
            <h4>
              To be successful in Project DUAL, complete these steps:
            </h4>
            <hr />
          </Container>

          <Container className={styles.pageContainer}>
            <h3>Getting Organized</h3>
            <Container className={styles.attributeContainerText}>
              <ul>
                <li>
                  <b>Create a Google Calendar or use a planner.</b> Use it to manage your trainings, meetings, and signups.
                </li>
                <li>
                  Update your calendar/planner every time you confirm a training or sign up for a meeting.
                </li>
                <li>
                  See more class info and materials—visit the <a href="https://bit.ly/3M2YUln" target="_blank" rel="noopener noreferrer">Project DUAL Class Page</a>.
                </li>
              </ul>
            </Container>
          </Container>

          <Container className={styles.pageContainer}>
            <h3>Communication</h3>
            <Container className={styles.attributeContainerText}>
              <ul>
                <li>
                  <b>Sign up and download Microsoft Teams on your laptop/phone.</b>
                </li>
                <li>
                  If not added to Project DUAL in Teams, message Dr. Su Yeong Kim: <br />
                  <i>“Hi Dr. Kim. My name is [Name] and I am a new RA requesting the code to access the Microsoft Teams platform for Project DUAL. My UT EID is: [UTEID].”</i>
                </li>
                <li>
                  Sign up for an Introductory Meeting and required Trainings (presentation, interview, transcription, GAV, HV, etc). You can train as a trainer for each.
                </li>
              </ul>
            </Container>
          </Container>

          <Container className={styles.pageContainer}>
            <h3>Required Meetings & Assignments</h3>
            <Container className={styles.attributeContainerText}>
              <ul>
                <li><b>5 Mandatory Class Meetings</b> with Dr. Kim (lectures)
                  <br />If missed, contact dualprojectut14@gmail.com to arrange a TA makeup meeting.
                </li>
                <li>
                  <b>5 Mandatory Discussion Sections</b> with leader (small group)
                  <ul>
                    <li>Double up on another leader’s section to make up missed meetings</li>
                  </ul>
                </li>
                <li>
                  <b>5 Mandatory Mentor Meetings</b> (1-on-1 or with 1 other person)
                  <ul>
                    <li>Make up by attending another mentor’s meeting (can double up).
                      <br /><b>NOTE:</b> You CANNOT double up Mentor Meeting #5.
                    </li>
                  </ul>
                </li>
                <li>
                  <b>Independent Study Assignment Essay (Reflection Paper)</b>
                </li>
                <li>
                  <b>Complete hours:</b> (125 or 250 depending on class)
                </li>
              </ul>
            </Container>
          </Container>

          <Container className={styles.pageContainer}>
            <h3>Guide to Meeting Types, Attendance, & Makeup</h3>
            <Container className={styles.attributeContainerText}>
              For a full guide and FAQs, see:<br />
              <a href="https://utexas.box.com/s/a8yhc54ib03zy9jkjoky1z7pbkke114g" target="_blank" rel="noopener noreferrer">
                Box: Types of Meetings, Attendance, Makeup Policies
              </a>
            </Container>
          </Container>

          <Container className={styles.pageContainer}>
            <h3>Reminders</h3>
            <Container className={styles.attributeContainerText}>
              <ul>
                <li>To avoid grade deductions, do the weekly WhenIsGood polls <b>before Tuesday</b>!</li>
                <li>Answer Project DUAL messages <b>within 24 hours</b>!</li>
              </ul>
            </Container>
          </Container>

        </Container>
      )}
    </div>
  );
};

export default NeedToKnowPage;
